import { useState, useEffect, useCallback, useRef } from 'react';
import DashboardService from '../services/DashboardService';
import { normalizeTemplateStructure } from '../utils/dashboardLayoutUtils';
import isEqual from 'lodash/isEqual';
import { useGlobalVariables } from '../contexts/GlobalVariablesContext';

export const useDashboardData = (specificDashboardId = null, isPopup = false, globalVariables = null, initialVariables = {}, options = {}) => {
  const [dashboardTemplate, setDashboardTemplateState] = useState(null);
  const [chartsData, setChartsData] = useState({});

  const disableIntervals = !!(options && options.disableIntervals);

  // GlobalVariables context 가져오기
  const globalVariablesContext = useGlobalVariables();
  const notifyLoadingComplete = globalVariablesContext?.notifyLoadingComplete;
  const beginRequest = globalVariablesContext?.beginRequest;
  const endRequest = globalVariablesContext?.endRequest;

  // globalVariables가 props로 전달되면 사용 (Dashboard 컴포넌트에서 전달)
  const getVariableValue = globalVariables?.getVariableValue || null;
  
  // useRef로 stable reference 유지
  const getVariableValueRef = useRef(getVariableValue);
  const initialVariablesRef = useRef(initialVariables);
  
  // 값이 변경될 때만 ref 업데이트
  useEffect(() => {
    getVariableValueRef.current = getVariableValue;
  }, [getVariableValue]);
  
  useEffect(() => {
    initialVariablesRef.current = initialVariables;
  }, [initialVariables]);
  // 2단계 로딩: 템플릿 로딩과 데이터 로딩 분리
  const [templateLoading, setTemplateLoading] = useState(true); // 템플릿 구조 로딩
  const [dataLoading, setDataLoading] = useState(true); // 차트 데이터 로딩
  const [error, setError] = useState(null);
  const timerRefs = useRef({});
  const apiService = useRef(DashboardService);
  const dashboardTemplateRef = useRef(null); // 최신 dashboardTemplate 상태 추적
  const componentStatesRef = useRef({}); // 컴포넌트별 현재 상태(클릭 파라미터 등) 추적
  const lastRequestKeyRef = useRef({}); // 컴포넌트별 마지막 로드 키(sql+params) 추적
  const dashboardIdRef = useRef(specificDashboardId); // 대시보드 ID 추적

  // 전체 로딩 상태 (템플릿 또는 데이터 중 하나라도 로딩 중이면 true)
  const loading = templateLoading || dataLoading;

  // dashboardTemplate 상태 변경 시 ref 업데이트
  useEffect(() => {
    dashboardTemplateRef.current = dashboardTemplate;
  }, [dashboardTemplate]);

  const loadChartData = useCallback(async (component, clickParams = null, fromInterval = false) => {
    if (!component) {
      console.warn('[useDashboardData] loadChartData called with null component');
      return Promise.resolve();
    }
    
    try {
      // 레이더 차트 디버깅
      if (component.chart_options?.viz_type === 'radar') {
      }

      // text_label의 SQL 타입 처리 - query 우선, 없을 때만 sql을 query로 매핑
      if (component.chart_options?.viz_type === 'text_label' && component.data_source?.type === 'sql') {
        const existingQuery = component.data_source.query || component.data_source.sql;
        // 쿼리가 전혀 없으면 에러 처리
        if (!existingQuery) {
          console.warn(`[useDashboardData] 텍스트 라벨 ${component.id}에 SQL 쿼리가 없습니다.`);
          setChartsData(prev => ({
            ...prev,
            [component.id]: {
              error: true,
              errorType: 'invalid_query',
              errorMessage: '유효한 SQL 쿼리가 없습니다',
              lastUpdated: new Date().toISOString(),
              onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
            }
          }));
          return Promise.resolve();
        }

        // query가 없고 sql만 있는 경우에만 보정
        if (!component.data_source.query) {
          const tempComponent = {
            ...component,
            data_source: {
              ...component.data_source,
              query: existingQuery
            }
          };
          component = tempComponent;
        }
      }

      if (component.data_source && component.data_source.type === 'static') {
        // webframe 타입인 경우 특별 처리
        if (component.chart_options?.viz_type === 'webframe' || component.type === 'webframe') {
          // URL 추출 로직: 직접 구조와 중첩 구조 모두 지원
          const extractUrl = (dataSource) => {
            // 직접 구조: data_source.url
            if (dataSource.url) {
              return dataSource.url;
            }
            // 중첩 구조: data_source.data.url
            if (dataSource.data && dataSource.data.url) {
              return dataSource.data.url;
            }
            return null;
          };
          
          const extractedUrl = extractUrl(component.data_source);
          const webframeData = {
            chart_data: {
              chart_options: {
                viz_type: 'webframe',
                title: component.chart_options?.title || component.title,
                options: component.chart_options?.options || {},
                data: {
                  url: extractedUrl
                }
              }
            },
            type: 'static',
            lastUpdated: new Date().toISOString()
          };
          
          setChartsData(prev => ({
            ...prev,
            [component.id]: {
              ...webframeData,
              onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
            }
          }));
        } else {
          // 기존 static 타입 처리 (text_label, markdown 등)
          setChartsData(prev => ({
            ...prev,
            [component.id]: {
              content: component.data_source.content,
              type: 'static',
              lastUpdated: new Date().toISOString(),
              onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
            }
          }));
        }
        return Promise.resolve();
      }

      if (!component.data_source || !component.data_source.query) {
        console.warn(`[useDashboardData] 컴포넌트 ${component.id}에 유효한 쿼리가 없습니다.`);
        setChartsData(prev => ({
          ...prev,
          [component.id]: {
            error: true,
            errorType: 'invalid_query',
            errorMessage: '유효한 쿼리가 없습니다',
            lastUpdated: new Date().toISOString(),
            onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
          }
        }));
        return Promise.resolve();
      }

      // 클릭 파라미터 처리
      // clickParams가 명시적으로 전달되면 그것을 사용 (null 포함)
      // clickParams가 undefined인 경우에만 컴포넌트에 저장된 값 사용
      const finalClickParams = clickParams !== undefined ? clickParams : component._click_params;
      
      // subscribed_variables가 있는 경우 처리
      let effectiveParams = finalClickParams;
      // DEBUG: subscribed_variables 확인
      if (component.id?.includes('table')) {
        console.log('[DEBUG] component.id:', component.id);
        console.log('[DEBUG] component.subscribed_variables:', component.subscribed_variables);
        console.log('[DEBUG] finalClickParams:', finalClickParams);
        console.log('[DEBUG] getVariableValueRef.current:', getVariableValueRef.current);
      }
      if (component.subscribed_variables) {
        effectiveParams = effectiveParams || {};
        
        Object.entries(component.subscribed_variables).forEach(([varName, config]) => {
          // 1. clickParams에 이미 값이 있으면 사용 (DashboardComponentRenderer에서 전달)
          if (effectiveParams[varName] !== undefined) {
          }
          // 2. GlobalVariables에서 현재 값 가져오기 (getVariableValue가 있는 경우)
          else if (getVariableValueRef.current) {
            const currentValue = getVariableValueRef.current(varName);
            if (currentValue !== undefined) {
              effectiveParams[varName] = currentValue;
            } else if (initialVariablesRef.current && initialVariablesRef.current[varName] !== undefined) {
              // 3. initialVariables에서 값 가져오기 (팝업 대시보드의 경우)
              effectiveParams[varName] = initialVariablesRef.current[varName];
            } else if (config.default !== undefined) {
              effectiveParams[varName] = config.default;
            }
          }
          // 3. getVariableValue가 없으면 initialVariables 또는 기본값 사용
          else if (initialVariablesRef.current && initialVariablesRef.current[varName] !== undefined) {
            effectiveParams[varName] = initialVariablesRef.current[varName];
          } else if (config.default !== undefined) {
            effectiveParams[varName] = config.default;
          }
        });

      }

      // DEBUG: effectiveParams 확인
      if (component.id?.includes('table')) {
        console.log('[DEBUG] effectiveParams after processing:', effectiveParams);
      }

      const params = {
        sql: component.data_source.query,
        chart_template: component,
        ...(effectiveParams && { query_params: effectiveParams })
      };

      // DEBUG: 최종 params 확인
      if (component.id?.includes('table')) {
        console.log('[DEBUG] final params.query_params:', params.query_params);
      }

      // 중복 요청 방지: 동일 sql + query_params로 연속 호출 시 스킵
      try {
        const requestKey = JSON.stringify({ sql: params.sql, query_params: params.query_params || null });
        const prevKey = lastRequestKeyRef.current[component.id];
        if (prevKey && prevKey === requestKey) {
          // 동일 요청이면 서버 호출 생략
          return Promise.resolve();
        }
        lastRequestKeyRef.current[component.id] = requestKey;
      } catch (e) {
        // 키 생성 실패 시 디듀프 없이 진행
      }

      // 요청 기반 오버레이: 타이머 갱신이 아닐 때만 로딩 표시
      const reqToken = (!fromInterval && beginRequest) ? beginRequest(component.id) : null;
      let response;
      try {
        response = await apiService.current.updateChartData(params);
      } finally {
        // 요청 종료(성공/실패/예외 무관)
        if (endRequest && reqToken) endRequest(reqToken);
      }
      
      if (response.success && response.data) {
        // 응답 데이터에 error 플래그가 있는지 확인
        // chart_data 내부의 error도 확인
        if (response.data.chart_data) {
        }
        
        // text_label의 경우 특별 처리
        if (component.chart_options?.viz_type === 'text_label' && response.data.query_result) {
        }
        
        // response.data에 error가 있으면 에러로 처리
        if (response.data.error === true) {
          console.warn(`[useDashboardData] Backend returned error flag for ${component.id}`);
          setChartsData(prev => ({
            ...prev,
            [component.id]: {
              error: true,
              errorType: response.data.errorType || 'api_error',
              errorMessage: response.data.errorMessage || '차트 데이터 처리 중 오류',
              chart_data: response.data.chart_data, // 차트 데이터는 있을 수 있음
              lastUpdated: new Date().toISOString(),
              onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
            }
          }));
        } else {
          setChartsData(prev => ({
            ...prev,
            [component.id]: {
              ...response.data,
              raw_data: response.data.raw_data || response.data.query_result, // raw_data 포함
              type: 'dynamic',
              lastUpdated: new Date().toISOString(),
              skipUpdateAnimation: fromInterval, // 타이머 갱신 시 애니메이션 스킵
              onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
            }
          }));
        }
      } else {
        console.error(`[useDashboardData] 컴포넌트 ${component.id} 차트 데이터 로드 실패:`, response.error);
        setChartsData(prev => ({
          ...prev,
          [component.id]: {
            error: true,
            errorType: 'api_error',
            errorMessage: response.error || '데이터 로드 실패',
            lastUpdated: new Date().toISOString(),
            onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
          }
        }));
      }
    } catch (err) {
      console.error(`[useDashboardData] 컴포넌트 ${component.id} 차트 데이터 로드 중 오류:`, err);
      setChartsData(prev => ({
        ...prev,
        [component.id]: {
          error: true,
          errorType: err.response?.status === 500 ? 'server' : 'client',
          errorMessage: err.message || '알 수 없는 오류',
          lastUpdated: new Date().toISOString(),
          onRenderComplete: notifyLoadingComplete ? () => notifyLoadingComplete(component.id) : undefined
        }
      }));
    }
  }, []); // Empty dependency array - using refs for stable references

  const loadAllChartsData = useCallback((components) => {
    // 기존 타이머 정리 - 컴포넌트별로 개별 정리
    Object.entries(timerRefs.current).forEach(([componentId, timer]) => {
      if (timer) {
        clearInterval(timer);
      }
    });
    timerRefs.current = {};

    if (!components || components.length === 0) {
      console.warn('[useDashboardData] loadAllChartsData: No components to load data for.');
      setChartsData({}); // Clear charts data if no components
      return;
    }
    
    components.forEach(component => {
      if (component && component.id) { // Ensure component and component.id exist
        loadChartData(component);
        const interval = component.updateInterval || 3600;
        if (interval > 0 && !disableIntervals) {
          // 중복 방지를 위해 기존 타이머가 있으면 먼저 정리
          if (timerRefs.current[component.id]) {
            clearInterval(timerRefs.current[component.id]);
          }
          timerRefs.current[component.id] = setInterval(() => {
            // 타이머 갱신은 항상 서버 호출하도록 디듀프 키 삭제
            delete lastRequestKeyRef.current[component.id];

            // 저장된 상태가 있으면 그것을 사용
            const savedState = componentStatesRef.current[component.id];
            if (savedState) {
              loadChartData(savedState.component, savedState.clickParams, true);
            } else {
              // 저장된 상태가 없으면 최신 컴포넌트 사용
              const latestComponent = dashboardTemplateRef.current?.components?.find(c => c.id === component.id);
              if (latestComponent) {
                loadChartData(latestComponent, null, true);
              } else {
                loadChartData(component, null, true);
              }
            }
          }, interval * 1000);
        }
      } else {
        console.warn('[useDashboardData] loadAllChartsData: Skipping invalid component.', component);
      }
    });
  }, [loadChartData]);

  // 개별 순차 로딩 함수
  const loadChartsSequentially = useCallback(async (components) => {
    if (!components || components.length === 0) {
      console.warn('[useDashboardData] loadChartsSequentially: No components to load.');
      setDataLoading(false);
      return;
    }

    // 기존 타이머 정리
    Object.values(timerRefs.current).forEach(timer => {
      if (timer) clearInterval(timer);
    });
    timerRefs.current = {};

    let loadedCount = 0;
    const totalComponents = components.length;

    // 먼저 모든 차트를 로딩 (인터벌 설정 없이)
    for (const component of components) {
      if (component && component.id) {
        try {
          await loadChartData(component);
          
          // 각 차트 로딩 완료 후 약간의 지연 (자연스러운 전환)
          await new Promise(resolve => setTimeout(resolve, 150));
          
          loadedCount++;
        } catch (error) {
          console.error(`[useDashboardData] 차트 로딩 실패: ${component.id}`, error);
          loadedCount++;
        }
      }
    }

    setDataLoading(false);
    // 모든 차트 로딩 완료 후 15초 지연하여 업데이트 인터벌 설정
    // 이렇게 하면 사용자가 차트를 충분히 본 후에 백그라운드 업데이트가 시작됨
    if (!disableIntervals) {
      setTimeout(() => {
        for (const component of components) {
          if (component && component.id) {
            const interval = component.updateInterval || 3600;
            if (interval > 0) {
              // 중복 방지를 위해 기존 타이머가 있으면 먼저 정리
              if (timerRefs.current[component.id]) {
                clearInterval(timerRefs.current[component.id]);
              }
              timerRefs.current[component.id] = setInterval(() => {
                // 타이머 갱신은 항상 서버 호출하도록 디듀프 키 삭제
                delete lastRequestKeyRef.current[component.id];

                // 저장된 상태가 있으면 그것을 사용
                const savedState = componentStatesRef.current[component.id];
                if (savedState) {
                  loadChartData(savedState.component, savedState.clickParams, true);
                } else {
                  // 저장된 상태가 없으면 최신 컴포넌트 사용
                  const latestComponent = dashboardTemplateRef.current?.components?.find(c => c.id === component.id);
                  if (latestComponent) {
                    loadChartData(latestComponent, null, true);
                  } else {
                    loadChartData(component, null, true);
                  }
                }
              }, interval * 1000);
            }
          }
        }
      }, 15000); // 15초 지연
    }
  }, [loadChartData]);

  useEffect(() => {
    const loadInitialDashboard = async () => {
      setTemplateLoading(true);
      setDataLoading(true);
      setError(null);
      
      try {
        // 1단계: 템플릿 구조만 빠르게 로드
        // 특정 대시보드 ID가 있으면 사용, 없으면 기본값
        const dashboardIdToLoad = specificDashboardId || 'system_default';
        // 팝업 모드에서는 strict 모드 사용 (폴백 없음)
        const response = await apiService.current.getDashboardTemplate(dashboardIdToLoad, isPopup);

        if (response.success && response.data) {
          let processedTemplate = { ...response.data };
          // Ensure normalization happens correctly, template might be nested
          if (processedTemplate.template && typeof processedTemplate.template === 'object') {
             processedTemplate = normalizeTemplateStructure(processedTemplate);
          } else if (processedTemplate.template && typeof processedTemplate.template === 'string') {
             processedTemplate = normalizeTemplateStructure(processedTemplate);
          } else if (!processedTemplate.components) { // If template is not nested but components are missing
             processedTemplate = normalizeTemplateStructure(processedTemplate);
          }

          // 백엔드에서 우선순위 로직으로 반환된 대시보드 사용
          const actualDashboardId = processedTemplate.dashboard_id || processedTemplate.dashboardId;
          // 컴포넌트 위치 확인
          if (processedTemplate.components && processedTemplate.components.length > 0) {
            processedTemplate.components.forEach(comp => {
            });
          }

          // 템플릿 설정 후 즉시 templateLoading 완료
          setDashboardTemplateState(processedTemplate);
          setTemplateLoading(false);
          // 2단계: 차트 데이터 로드 (비동기로 진행)
          const componentsArray = processedTemplate.components || [];
          const validComponents = componentsArray.filter(comp => comp && comp.id);
          
          if (validComponents.length > 0) {
            // 모든 차트를 병렬로 로드
            const loadPromises = validComponents.map(component => {
              if (component && component.id) {
                return loadChartData(component).catch(err => {
                  console.error(`[useDashboardData] 차트 ${component.id} 로드 실패:`, err);
                  // 에러가 발생해도 다른 차트는 계속 로드
                  return null;
                });
              }
              return Promise.resolve(null);
            });
            
            // 모든 차트 로드가 완료되면 dataLoading을 false로 설정
            Promise.all(loadPromises).then(() => {
              setDataLoading(false);
              
              // 인터벌 설정
              if (!disableIntervals) {
                validComponents.forEach(component => {
                  if (component && component.id) {
                    const interval = component.updateInterval || 3600;
                    if (interval > 0) {
                      // 중복 방지를 위해 기존 타이머가 있으면 먼저 정리
                      if (timerRefs.current[component.id]) {
                        clearInterval(timerRefs.current[component.id]);
                      }
                      timerRefs.current[component.id] = setInterval(() => {
                        // 타이머 갱신은 항상 서버 호출하도록 디듀프 키 삭제
                        delete lastRequestKeyRef.current[component.id];

                        // 저장된 상태가 있으면 그것을 사용
                        const savedState = componentStatesRef.current[component.id];
                        if (savedState) {
                          loadChartData(savedState.component, savedState.clickParams, true);
                        } else {
                          // 저장된 상태가 없으면 최신 컴포넌트 사용
                          const latestComponent = dashboardTemplateRef.current?.components?.find(c => c.id === component.id);
                          if (latestComponent) {
                            loadChartData(latestComponent, null, true);
                          } else {
                            loadChartData(component, null, true);
                          }
                        }
                      }, interval * 1000);
                    }
                  }
                });
              }
            });
            
          } else {
            console.warn('[useDashboardData] No valid components found in initial template.');
            setChartsData({});
            setDataLoading(false);
            // Clear timers too if no components
            Object.values(timerRefs.current).forEach(timer => clearInterval(timer));
            timerRefs.current = {};
          }
        } else {
          // 에러 객체 구조화 (Dashboard 컴포넌트가 기대하는 형태)
          console.error('[useDashboardData] 대시보드 로드 실패:', {
            dashboardId: specificDashboardId,
            isPopup,
            response
          });
          const errorObj = {
            message: response.error || 'Failed to load dashboard template.',
            status: response.status || 'not_found'
          };
          setError(errorObj);
          setTemplateLoading(false);
          setDataLoading(false);
        }
      } catch (err) {
        console.error('[useDashboardData] Exception during initial load:', err);
        // 에러 객체 구조화
        const errorObj = {
          message: err.message || 'An error occurred while loading the dashboard template.',
          status: err.response?.status || 'error'
        };
        setError(errorObj);
        setTemplateLoading(false);
        setDataLoading(false);
      }
    };

    loadInitialDashboard();

    return () => {
      Object.values(timerRefs.current).forEach(timer => {
        if (timer) clearInterval(timer);
      });
      timerRefs.current = {};
    };
  }, [loadAllChartsData]); // normalizeTemplateStructure is pure, so not needed if used correctly

  const setDashboardTemplateAndUpdateData = useCallback((newTemplateData) => {
    
    const previousTemplate = dashboardTemplateRef.current; // ref를 사용하여 최신 상태 참조

    let normalizedNewTemplate = newTemplateData;
    if (newTemplateData && (newTemplateData.template || !newTemplateData.components)) {
        normalizedNewTemplate = normalizeTemplateStructure(newTemplateData);
    }

    // 데이터 로딩 및 타이머 재설정 여부 결정
    let shouldReloadAllData = true;

    if (previousTemplate && normalizedNewTemplate && 
        previousTemplate.components && normalizedNewTemplate.components &&
        previousTemplate.components.length === normalizedNewTemplate.components.length) {
      
      let onlyPositionChanged = true;
      const prevCompsMap = new Map(previousTemplate.components.map(c => [c.id, c]));

      // z-order 변경 감지를 위해 컴포넌트 순서 확인
      let orderChanged = false;
      for (let i = 0; i < normalizedNewTemplate.components.length; i++) {
        if (previousTemplate.components[i]?.id !== normalizedNewTemplate.components[i]?.id) {
          orderChanged = true;
          break;
        }
      }

      // 순서만 변경된 경우 (z-order 변경)
      if (orderChanged) {
        // z-order 변경은 템플릿만 업데이트하고 데이터는 리로드하지 않음
        setDashboardTemplateState(normalizedNewTemplate);
        return; // 여기서 함수 종료하여 데이터 리로드 방지
      } else {
        for (const newComp of normalizedNewTemplate.components) {
          if (!newComp || !newComp.id) continue; // Skip invalid new components

          const prevComp = prevCompsMap.get(newComp.id);
          if (!prevComp) { // 새 컴포넌트 추가됨 (ID 기준)
            onlyPositionChanged = false;
            break;
          }

          // position 제외한 객체 비교
          const { position: prevPos, ...prevCompWithoutPos } = prevComp;
          const { position: newPos, ...newCompWithoutPos } = newComp;

          if (!isEqual(prevCompWithoutPos, newCompWithoutPos)) {
            onlyPositionChanged = false;
            break;
          }
          // 여기서는 position 값 자체의 변경은 "onlyPositionChanged"에 영향을 주지 않음.
          // 즉, position만 바뀌고 다른 모든 것이 같다면 onlyPositionChanged는 true로 유지됨.
        }
      }
      
      // 모든 컴포넌트가 존재하고, position 이외의 속성이 변경되지 않았다면 데이터 리로딩 불필요
      if (onlyPositionChanged) {
        // 컴포넌트 ID 목록이 완전히 같은지 한번 더 확인 (순서 변경 등도 감지 위함 - isEqual이 객체 배열 순서도 고려)
        const prevCompIds = previousTemplate.components.map(c => c.id).sort();
        const newCompIds = normalizedNewTemplate.components.map(c => c.id).sort();
        if (isEqual(prevCompIds, newCompIds)) {
            shouldReloadAllData = false;
        } else {
            onlyPositionChanged = false; // ID 목록이 다르면, position만 바뀐게 아님.
        }

      } else {
      }
    } else if (previousTemplate && normalizedNewTemplate && previousTemplate.components && normalizedNewTemplate.components && previousTemplate.components.length !== normalizedNewTemplate.components.length) {
        // shouldReloadAllData는 이미 true
    } else if (!previousTemplate && normalizedNewTemplate) {
        // shouldReloadAllData는 이미 true
    }

    setDashboardTemplateState(normalizedNewTemplate); // 템플릿 상태는 항상 업데이트

    if (shouldReloadAllData) {
      // Clear all existing timers before setting new ones
      Object.values(timerRefs.current).forEach(timer => {
        if (timer) clearInterval(timer);
      });
      timerRefs.current = {};
      // 디듀프 키 초기화 (대시보드 전환 시 이전 요청 키 영향 제거)
      lastRequestKeyRef.current = {};
      
      // Clear existing chart data to ensure no stale data from previous template
      setChartsData({});

      if (normalizedNewTemplate && normalizedNewTemplate.components && normalizedNewTemplate.components.length > 0) {
        const validComponents = normalizedNewTemplate.components.filter(comp => comp && comp.id);
        if (validComponents.length > 0) {
          loadAllChartsData(validComponents);
        } else {
           console.warn('[useDashboardData] setDashboardTemplateAndUpdateData: No valid components in new template for full reload.');
        }
      } else {
        console.warn('[useDashboardData] setDashboardTemplateAndUpdateData: New template has no components for full reload.');
      }
    } else {
        // shouldReloadAllData is false.
        // 템플릿의 position만 변경되었으므로, 차트 데이터와 타이머는 그대로 유지.
        // 단, dashboardTemplate 상태는 위에서 이미 업데이트 되었으므로,
        // React Grid Layout은 새 position 정보를 가지고 렌더링됨.
    }
  }, [loadAllChartsData]); // dashboardTemplate 의존성 제거하여 무한 리렌더링 방지

  const pauseDataUpdates = useCallback(() => {
    Object.values(timerRefs.current).forEach(timer => {
      if (timer) clearInterval(timer);
    });
    // We keep timerRefs populated but clear intervals. 
    // Alternatively, clear timerRefs.current = {} if resume doesn't rely on old IDs.
    // For simplicity, just clearing intervals is fine if resumeTimers re-initializes based on current template.
  }, []);

  const resumeDataUpdates = useCallback(() => {
    const currentTemplate = dashboardTemplateRef.current;
    if (currentTemplate && currentTemplate.components) {
      const validComponents = currentTemplate.components.filter(comp => comp && comp.id);
      if (validComponents.length > 0) {
        loadAllChartsData(validComponents);
      }
    }
  }, [loadAllChartsData]);

  // 전체 차트 데이터 새로고침: 현재 템플릿의 모든 컴포넌트를 재조회
  const refreshAllChartsData = useCallback(() => {
    const currentTemplate = dashboardTemplateRef.current;
    if (!currentTemplate || !currentTemplate.components) {
      console.warn('[useDashboardData] refreshAllChartsData: No dashboard template available');
      return;
    }

    const validComponents = currentTemplate.components.filter(comp => comp && comp.id);
    if (validComponents.length === 0) {
      console.warn('[useDashboardData] refreshAllChartsData: No valid components');
      return;
    }

    validComponents.forEach((component) => {
      // 저장된 상태가 있으면 clickParams 유지하여 호출
      const savedState = componentStatesRef.current[component.id];
      if (savedState) {
        const nextParams = {
          ...(savedState.clickParams || {}),
          __refresh_ts: Date.now()
        };
        loadChartData(savedState.component, nextParams);
      } else {
        // 최신 컴포넌트 참조 확보 후 호출
        const latest = dashboardTemplateRef.current?.components?.find(c => c.id === component.id) || component;
        loadChartData(latest, { __refresh_ts: Date.now() });
      }
    });
  }, [loadChartData]);

  /**
   * 새로운 대시보드 ID로 템플릿을 다시 로드합니다.
   * 페이지 새로고침 없이 대시보드를 전환할 때 사용됩니다.
   * @param {string} newDashboardId - 로드할 대시보드 ID
   */
  const latestRequestIdRef = useRef(0);

  const reloadDashboardTemplate = useCallback(async (newDashboardId) => {
    const requestId = ++latestRequestIdRef.current;
    setTemplateLoading(true);
    setDataLoading(true);
    setError(null);
    
    try {
      // 기존 타이머들 정리
      Object.values(timerRefs.current).forEach(timer => {
        if (timer) clearInterval(timer);
      });
      timerRefs.current = {};
      
      // 기존 차트 데이터 정리
      setChartsData({});
      // 디듀프 키 초기화 (대시보드 재로드 시 이전 요청 키 영향 제거)
      lastRequestKeyRef.current = {};
      
      // 새 대시보드 템플릿 로드
      // 일반 화면 전환에서는 strict 사용하지 않음(백엔드가 last_accessed 업데이트하도록)
      // 팝업에서는 strict(no_fallback) 사용하여 히스토리 업데이트 방지
      const response = await apiService.current.getDashboardTemplate(
        newDashboardId || 'system_default',
        isPopup
      );

      if (response.success && response.data) {
        if (requestId !== latestRequestIdRef.current) {
          console.warn('[useDashboardData] Stale reload response ignored. id=', requestId, 'latest=', latestRequestIdRef.current);
          return { success: false, error: 'stale_response' };
        }
        let processedTemplate = { ...response.data };
        
        // 템플릿 정규화
        if (processedTemplate.template && typeof processedTemplate.template === 'object') {
          processedTemplate = normalizeTemplateStructure(processedTemplate);
        } else if (processedTemplate.template && typeof processedTemplate.template === 'string') {
          processedTemplate = normalizeTemplateStructure(processedTemplate);
        } else if (!processedTemplate.components) {
          processedTemplate = normalizeTemplateStructure(processedTemplate);
        }
        
        // 백엔드에서 받은 컴포넌트 위치 확인
        processedTemplate.components?.forEach(comp => {
          if (comp.id && comp.id.includes('pie')) {
          }
        });

        // 최신 요청만 상태 반영
        if (requestId !== latestRequestIdRef.current) {
          console.warn('[useDashboardData] Stale state update skipped. id=', requestId, 'latest=', latestRequestIdRef.current);
          return { success: false, error: 'stale_response' };
        }
        setDashboardTemplateState(processedTemplate);
        setTemplateLoading(false);
        
        const componentsArray = processedTemplate.components || [];
        const validComponents = componentsArray.filter(comp => comp && comp.id);
        
        if (validComponents.length > 0) {
          // 기존 타이머 정리
          Object.values(timerRefs.current).forEach(timer => {
            if (timer) clearInterval(timer);
          });
          timerRefs.current = {};
          
          // 모든 차트를 병렬로 로드
          const loadPromises = validComponents.map(component => {
            if (component && component.id) {
              return loadChartData(component).catch(err => {
                console.error(`[useDashboardData] 차트 ${component.id} 로드 실패:`, err);
                return null;
              });
            }
            return Promise.resolve(null);
          });
          
          // 모든 차트 로드가 완료되면 dataLoading을 false로 설정
          Promise.all(loadPromises).then(() => {
            if (requestId !== latestRequestIdRef.current) {
              console.warn('[useDashboardData] Stale data loading completion ignored. id=', requestId, 'latest=', latestRequestIdRef.current);
              return;
            }
            setDataLoading(false);
            
            // 인터벌 설정
            if (!disableIntervals) {
              validComponents.forEach(component => {
                if (component && component.id) {
                  const interval = component.updateInterval || 3600;
                  if (interval > 0) {
                    // 중복 방지를 위해 기존 타이머가 있으면 먼저 정리
                    if (timerRefs.current[component.id]) {
                      clearInterval(timerRefs.current[component.id]);
                    }
                    timerRefs.current[component.id] = setInterval(() => {
                      if (requestId !== latestRequestIdRef.current) {
                        // 오래된 타이머는 자연 소멸되도록 즉시 반환
                        return;
                      }

                      // 타이머 갱신은 항상 서버 호출하도록 디듀프 키 삭제
                      delete lastRequestKeyRef.current[component.id];

                      // 저장된 상태가 있으면 그것을 사용
                      const savedState = componentStatesRef.current[component.id];
                      if (savedState) {
                        loadChartData(savedState.component, savedState.clickParams, true);
                      } else {
                        // 저장된 상태가 없으면 최신 컴포넌트 사용
                        const latestComponent = dashboardTemplateRef.current?.components?.find(c => c.id === component.id);
                        if (latestComponent) {
                          loadChartData(latestComponent, null, true);
                        } else {
                          loadChartData(component, null, true);
                        }
                      }
                    }, interval * 1000);
                  }
                }
              });
            }
          });
        } else {
          console.warn('[useDashboardData] 새로 로드된 템플릿에 유효한 컴포넌트가 없습니다.');
          setDataLoading(false);
        }
        
        return { success: true, template: processedTemplate };
      } else {
        const errorMsg = response.error || '대시보드 템플릿 로드 실패';
        setError(errorMsg);
        console.error('[useDashboardData] 새로운 대시보드 템플릿 로드 실패:', errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const errorMsg = '대시보드 템플릿 로드 중 오류가 발생했습니다.';
      console.error('[useDashboardData] 새로운 대시보드 템플릿 로드 중 오류:', err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      if (requestId !== latestRequestIdRef.current) {
        // 오래된 요청의 마무리 처리 무시
        return;
      }
      setTemplateLoading(false);
      setDataLoading(false);
    }
  }, [loadAllChartsData]);
  
  // 초기 globalVariables 기반 재로드 제거: 초기 로드는 useDashboardData의 병렬 로드에서만 수행
  
  // refreshChartData 함수 추가 - 컴포넌트 ID로 특정 차트 데이터만 업데이트
  // clickParams는 이제 일반 클릭 파라미터 또는 구독 변수 값이 될 수 있음
  const refreshChartData = useCallback((componentId, clickParams = undefined, customComponent = null) => {
    if (!dashboardTemplateRef.current || !dashboardTemplateRef.current.components) {
      console.warn('[useDashboardData] refreshChartData: No dashboard template available');
      return;
    }
    
    // customComponent가 제공되면 그것을 사용, 아니면 템플릿에서 찾기
    let component = customComponent || dashboardTemplateRef.current.components.find(c => c.id === componentId);
    if (component) {
      // clickParams가 null로 명시적으로 전달되면 _click_params를 제거한 깨끗한 컴포넌트 사용
      if (clickParams === null) {
        // customComponent가 제공된 경우에도 _click_params 제거
        const { _click_params, ...cleanComponent } = component;
        component = cleanComponent;
      }
      
      // 컴포넌트 상태 저장
      if (customComponent || clickParams !== undefined) {
        componentStatesRef.current[componentId] = {
          component: customComponent || component,
          clickParams: clickParams
        };
      } else if (clickParams === null) {
        // null이 명시적으로 전달되면 상태 제거
        delete componentStatesRef.current[componentId];
      }
      
      loadChartData(component, clickParams);
    } else {
      console.warn(`[useDashboardData] refreshChartData: Component ${componentId} not found`);
    }
  }, [loadChartData]);

  // 특정 컴포넌트들만 업데이트하는 새로운 함수
  const updateSpecificComponents = useCallback((componentsToUpdate) => {
    if (!dashboardTemplateRef.current) return;
    
    
    // 템플릿을 업데이트하지 않고, 데이터만 업데이트
    // 이렇게 하면 컴포넌트의 구조적 변경 없이 데이터만 새로고침됨
    componentsToUpdate.forEach(component => {
      // 현재 템플릿에서 해당 컴포넌트 찾기
      const currentComponent = dashboardTemplateRef.current.components.find(c => c.id === component.id);
      if (currentComponent) {
        // 클릭 파라미터를 포함한 컴포넌트로 데이터 로드
        const componentWithClickParams = {
          ...currentComponent,
          ...(component._click_params && { _click_params: component._click_params })
        };
        
        if (component._click_params) {
          loadChartData(componentWithClickParams, component._click_params);
        } else {
          loadChartData(componentWithClickParams);
        }
      }
    });
  }, [loadChartData]);

  return {
    dashboardTemplate,
    chartsData,
    loading, // 전체 로딩 상태 (템플릿 또는 데이터 로딩 중)
    templateLoading, // 템플릿 구조 로딩 상태
    dataLoading, // 차트 데이터 로딩 상태
    error,
    setDashboardTemplateAndUpdateData,
    pauseDataUpdates,
    resumeDataUpdates,
    refreshChartData, // 특정 차트 데이터 업데이트 함수
    reloadDashboardTemplate, // 새로운 대시보드 템플릿 로드 함수 추가
    updateSpecificComponents, // 특정 컴포넌트들만 업데이트하는 함수
    refreshAllChartsData, // 전체 차트 데이터 새로고침 함수
  };
}; 